import React, { useState, useEffect } from 'react';
import { collection, addDoc, getDocs, updateDoc, doc } from 'firebase/firestore';
import { db } from '../../config/firebaseConfig';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FaEdit, FaPlus, FaMinus, FaCheckCircle, FaTimesCircle, FaCreditCard, FaUniversity, FaRegBuilding, FaMoneyBillWave } from 'react-icons/fa';

const BankAccountManagementPage = () => {
  const [isFormVisible, setIsFormVisible] = useState(false);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [formData, setFormData] = useState({
    firma: '',
    sube: '',
    hesapNo: '',
    dosyaAciklama: '',
    odemeKesinti: '',
    odemeTuru: '',
    odemeAciklama: '',
    odemeDonemi: '',
    durum: '',
  });

  const [errors, setErrors] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchBankAccounts();
  }, []);

  const fetchBankAccounts = async () => {
    setIsLoading(true);
    try {
      const querySnapshot = await getDocs(collection(db, 'BankAccount'));
      const accounts = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setBankAccounts(accounts);
    } catch (error) {
      toast.error('Hesaplar yüklenirken hata oluştu: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    Object.keys(formData).forEach(key => {
      if (!formData[key].trim()) {
        newErrors[key] = 'Bu alan zorunludur';
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    
    // Clear error for this field when typing
    if (errors[name]) {
      setErrors({...errors, [name]: null});
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Lütfen tüm alanları doldurunuz.');
      return;
    }

    try {
      await addDoc(collection(db, 'BankAccount'), formData);
      toast.success('Kayıt başarıyla eklendi!');
      await fetchBankAccounts(); // Refresh the list after adding
      setFormData({
        firma: '',
        sube: '',
        hesapNo: '',
        dosyaAciklama: '',
        odemeKesinti: '',
        odemeTuru: '',
        odemeAciklama: '',
        odemeDonemi: '',
        durum: '',
      });
      setErrors({});
      setIsFormVisible(false); // Close the form after successful submission
    } catch (error) {
      toast.error('Bir hata oluştu: ' + error.message);
      console.error('Error saving data:', error);
    }
  };

  const toggleForm = () => {
    setIsFormVisible(!isFormVisible);
    if (!isFormVisible) {
      // Reset form when opening
      setFormData({
        firma: '',
        sube: '',
        hesapNo: '',
        dosyaAciklama: '',
        odemeKesinti: '',
        odemeTuru: '',
        odemeAciklama: '',
        odemeDonemi: '',
        durum: '',
      });
      setErrors({});
    }
  };

  const handleEdit = (account) => {
    setEditingId(account.id);
    setFormData(account);
  };

  const handleUpdate = async () => {
    if (!validateForm()) {
      toast.error('Lütfen tüm alanları doldurunuz.');
      return;
    }

    try {
      await updateDoc(doc(db, 'BankAccount', editingId), formData);
      toast.success('Kayıt başarıyla güncellendi!');
      await fetchBankAccounts();
      setEditingId(null);
      setFormData({
        firma: '',
        sube: '',
        hesapNo: '',
        dosyaAciklama: '',
        odemeKesinti: '',
        odemeTuru: '',
        odemeAciklama: '',
        odemeDonemi: '',
        durum: '',
      });
    } catch (error) {
      toast.error('Güncelleme sırasında hata oluştu: ' + error.message);
    }
  };

  const handleCancel = () => {
    setEditingId(null);
    setFormData({
      firma: '',
      sube: '',
      hesapNo: '',
      dosyaAciklama: '',
      odemeKesinti: '',
      odemeTuru: '',
      odemeAciklama: '',
      odemeDonemi: '',
      durum: '',
    });
  };

  // Function to determine status badge color
  const getStatusColor = (status) => {
    const statusLower = status.toLowerCase();
    if (statusLower.includes('aktif') || statusLower.includes('açık')) return '#38a169';
    if (statusLower.includes('pasif') || statusLower.includes('kapalı')) return '#e53e3e';
    if (statusLower.includes('bekle')) return '#dd6b20';
    return '#3182ce'; // default blue
  };

  return (
    <div style={{ 
      background: 'var(--page-bg, #f5f7fa)',
      padding: '40px 20px',
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '25px',
      color: 'var(--text-color, #333333)',
      fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"
    }}>
      <ToastContainer 
        position="top-right" 
        autoClose={3000} 
        theme="colored" 
        style={{ fontSize: '14px' }}
      />
      
      <div style={{
        display: 'flex',
        width: '100%',
        maxWidth: '1200px',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <h1 style={{
          fontSize: '1.75rem',
          fontWeight: '700',
          margin: '0',
          color: 'var(--title-color, #1a202c)',
          display: 'flex',
          alignItems: 'center',
          gap: '15px'
        }}>
          <FaUniversity style={{ color: '#3182ce' }} />
          <span>Banka Hesapları</span>
        </h1>
        
        <button 
          onClick={toggleForm} 
          style={{
            backgroundColor: isFormVisible ? '#e53e3e' : '#3182ce',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            padding: '0.75rem 1.25rem',
            fontSize: '0.95rem',
            fontWeight: '600',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            boxShadow: '0 2px 6px rgba(0, 0, 0, 0.1)',
            transition: 'all 0.2s ease',
          }}
          onMouseOver={(e) => {
            e.target.style.backgroundColor = isFormVisible ? '#c53030' : '#2b6cb0';
            e.target.style.transform = 'translateY(-2px)';
            e.target.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.1)';
          }}
          onMouseOut={(e) => {
            e.target.style.backgroundColor = isFormVisible ? '#e53e3e' : '#3182ce';
            e.target.style.transform = 'translateY(0)';
            e.target.style.boxShadow = '0 2px 6px rgba(0, 0, 0, 0.1)';
          }}
        >
          {isFormVisible ? (
            <>
              <FaMinus size={14} /> Formu Kapat
            </>
          ) : (
            <>
              <FaPlus size={14} /> Yeni Hesap Ekle
            </>
          )}
        </button>
      </div>

      {/* Form Container */}
      <div style={{
        width: '100%',
        maxWidth: '1200px',
        backgroundColor: 'var(--content-bg, #ffffff)',
        borderRadius: '12px',
        padding: isFormVisible ? '30px' : '0',
        boxShadow: 'var(--card-shadow, 0 4px 15px rgba(0, 0, 0, 0.05))',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        opacity: isFormVisible ? '1' : '0',
        visibility: isFormVisible ? 'visible' : 'hidden',
        maxHeight: isFormVisible ? '2000px' : '0',
        overflow: 'hidden',
        marginTop: isFormVisible ? '10px' : '0',
        border: '1px solid var(--border-color, #e2e8f0)'
      }}>
        <h2 style={{ 
          color: 'var(--title-color, #1a202c)', 
          marginBottom: '30px',
          textAlign: 'center',
          fontSize: '1.5rem',
          fontWeight: '600',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '10px'
        }}>
          <FaCreditCard style={{ color: '#3182ce' }} />
          Hesap Tanımlama Formu
        </h2>
        
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px' }}>
            {[
              [
                { label: 'Firma', name: 'firma', icon: <FaRegBuilding size={16} /> },
                { label: 'Firma Şube', name: 'sube', icon: <FaUniversity size={16} /> }
              ],
              [
                { label: 'Firma Hesap No / IBAN', name: 'hesapNo', icon: <FaCreditCard size={16} /> },
                { label: 'Dosya Açıklaması', name: 'dosyaAciklama', icon: null }
              ],
              [
                { label: 'Ödeme/Kesinti', name: 'odemeKesinti', icon: <FaMoneyBillWave size={16} /> },
                { label: 'Ödeme Türü', name: 'odemeTuru', icon: null }
              ],
              [
                { label: 'Ödeme Açıklama(Ödeme Türü Diğer ise)', name: 'odemeAciklama', icon: null },
                { label: 'Ödeme Dönemi', name: 'odemeDonemi', icon: null }
              ],
              [
                { label: 'Durum', name: 'durum', icon: null },
                { label: '', name: '' } // Empty field for alignment
              ]
            ].map((row, idx) => (
              <div key={idx} style={{ 
                display: 'flex', 
                gap: '20px',
                width: '100%'
              }}>
                {row.map((field) => field.name && (
                  <div key={field.name} style={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    gap: '10px',
                    flex: 1,
                    position: 'relative'
                  }}>
                    <label style={{ 
                      color: 'var(--secondary-text, #718096)', 
                      fontSize: '15px',
                      fontWeight: '500',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      {field.icon && <span style={{ color: '#3182ce' }}>{field.icon}</span>}
                      {field.label}
                    </label>
                    <input
                      type="text"
                      name={field.name}
                      value={formData[field.name]}
                      onChange={handleChange}
                      style={{
                        backgroundColor: 'var(--input-bg, #ffffff)',
                        color: 'var(--text-color, #333333)',
                        border: `1px solid ${errors[field.name] ? '#e53e3e' : 'var(--border-color, #e2e8f0)'}`,
                        borderRadius: '8px',
                        padding: '0.75rem 1rem',
                        fontSize: '0.95rem',
                        transition: 'all 0.2s ease',
                        outline: 'none',
                        width: '100%',
                        boxShadow: errors[field.name] ? '0 0 0 1px #e53e3e' : 'none',
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = '#3182ce';
                        e.target.style.boxShadow = '0 0 0 3px rgba(49, 130, 206, 0.2)';
                      }}
                      onBlur={(e) => {
                        if (errors[field.name]) {
                          e.target.style.borderColor = '#e53e3e';
                        } else {
                          e.target.style.borderColor = 'var(--border-color, #e2e8f0)';
                        }
                        e.target.style.boxShadow = 'none';
                      }}
                      placeholder={`${field.label} giriniz`}
                    />
                    {errors[field.name] && (
                      <span style={{
                        color: '#e53e3e',
                        fontSize: '12px',
                        marginTop: '-5px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '5px'
                      }}>
                        <FaTimesCircle size={12} />
                        {errors[field.name]}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            ))}
          </div>
          
          <button 
            type="submit" 
            style={{
              backgroundColor: '#3182ce',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              padding: '0.75rem 1.25rem',
              fontSize: '0.95rem',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              marginTop: '15px',
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '10px',
              boxShadow: '0 2px 6px rgba(0, 0, 0, 0.1)',
            }}
            onMouseOver={(e) => {
              e.target.style.backgroundColor = '#2b6cb0';
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.1)';
            }}
            onMouseOut={(e) => {
              e.target.style.backgroundColor = '#3182ce';
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 2px 6px rgba(0, 0, 0, 0.1)';
            }}
          >
            <FaCheckCircle size={18} />
            Kaydet
          </button>
        </form>
      </div>

      {/* Bank Accounts List */}
      {isLoading ? (
        <div style={{
          width: '100%',
          maxWidth: '1200px',
          textAlign: 'center',
          padding: '50px',
          backgroundColor: 'var(--content-bg, #ffffff)',
          borderRadius: '12px',
          boxShadow: 'var(--card-shadow, 0 4px 15px rgba(0, 0, 0, 0.05))'
        }}>
          <div style={{ 
            fontSize: '16px',
            color: 'var(--secondary-text, #718096)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '15px'
          }}>
            <div style={{
              width: '40px',
              height: '40px',
              border: '3px solid rgba(49, 130, 206, 0.2)',
              borderTop: '3px solid #3182ce',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
            }}></div>
            <style>
              {`
                @keyframes spin {
                  0% { transform: rotate(0deg); }
                  100% { transform: rotate(360deg); }
                }
              `}
            </style>
            Hesaplar yükleniyor...
          </div>
        </div>
      ) : bankAccounts.length > 0 ? (
        <div style={{
          width: '100%',
          maxWidth: '1200px',
          backgroundColor: 'var(--content-bg, #ffffff)',
          borderRadius: '12px',
          padding: '30px',
          boxShadow: 'var(--card-shadow, 0 4px 15px rgba(0, 0, 0, 0.05))',
          border: '1px solid var(--border-color, #e2e8f0)'
        }}>
          <h2 style={{ 
            color: 'var(--title-color, #1a202c)', 
            marginBottom: '30px',
            textAlign: 'center',
            fontSize: '1.5rem',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '10px'
          }}>
            <FaUniversity style={{ color: '#3182ce' }} />
            Kayıtlı Hesaplar
          </h2>

          <div style={{ 
            display: 'grid', 
            gap: '25px',
            gridTemplateColumns: 'repeat(auto-fill, minmax(500px, 1fr))',
          }}>
            {bankAccounts.map((account) => (
              <div key={account.id} style={{
                backgroundColor: 'var(--content-bg, #ffffff)',
                borderRadius: '12px',
                padding: '25px',
                color: 'var(--text-color, #333333)',
                position: 'relative',
                transition: 'all 0.3s ease',
                boxShadow: 'var(--card-shadow, 0 4px 15px rgba(0, 0, 0, 0.05))',
                border: '1px solid var(--border-color, #e2e8f0)',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                gap: editingId === account.id ? '15px' : '0',
              }}>
                {/* Decorative element */}
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '5px',
                  background: 'linear-gradient(90deg, #3182ce, #4299e1)'
                }}></div>
              
                {editingId === account.id ? (
                  <>
                    <h3 style={{ 
                      margin: '10px 0 20px 0',
                      fontSize: '1.3rem',
                      fontWeight: '600',
                      color: 'var(--title-color, #1a202c)',
                      textAlign: 'center'
                    }}>Hesap Düzenle</h3>
                    
                    <div style={{ 
                      display: 'grid', 
                      gridTemplateColumns: '1fr 1fr', 
                      gap: '15px',
                      marginBottom: '10px'
                    }}>
                      {Object.entries(formData)
                        .filter(([key]) => key !== 'id')
                        .map(([key, value]) => (
                          <div key={key} style={{ 
                            display: 'flex', 
                            flexDirection: 'column', 
                            gap: '5px'
                          }}>
                            <label style={{ 
                              fontSize: '13px', 
                              color: 'var(--secondary-text, #718096)', 
                              fontWeight: '500' 
                            }}>
                              {key.charAt(0).toUpperCase() + key.slice(1)}:
                            </label>
                            <input
                              type="text"
                              value={value}
                              onChange={(e) => setFormData({ ...formData, [key]: e.target.value })}
                              style={{
                                backgroundColor: 'var(--input-bg, #ffffff)',
                                color: 'var(--text-color, #333333)',
                                border: `1px solid ${errors[key] ? '#e53e3e' : 'var(--border-color, #e2e8f0)'}`,
                                borderRadius: '8px',
                                padding: '0.75rem 1rem',
                                fontSize: '0.95rem',
                                width: '100%',
                                outline: 'none',
                                transition: 'all 0.2s ease',
                              }}
                              onFocus={(e) => {
                                e.target.style.borderColor = '#3182ce';
                                e.target.style.boxShadow = '0 0 0 3px rgba(49, 130, 206, 0.2)';
                              }}
                              onBlur={(e) => {
                                if (errors[key]) {
                                  e.target.style.borderColor = '#e53e3e';
                                } else {
                                  e.target.style.borderColor = 'var(--border-color, #e2e8f0)';
                                }
                                e.target.style.boxShadow = 'none';
                              }}
                            />
                            {errors[key] && (
                              <span style={{
                                color: '#e53e3e',
                                fontSize: '12px',
                                marginTop: '2px'
                              }}>
                                {errors[key]}
                              </span>
                            )}
                          </div>
                        ))}
                    </div>
                    <div style={{ 
                      display: 'flex', 
                      gap: '10px', 
                      justifyContent: 'flex-end',
                      marginTop: '10px' 
                    }}>
                      <button
                        onClick={handleCancel}
                        style={{
                          backgroundColor: '#f7fafc',
                          color: '#4a5568',
                          border: '1px solid var(--border-color, #e2e8f0)',
                          borderRadius: '8px',
                          padding: '0.6rem 1rem',
                          cursor: 'pointer',
                          fontSize: '0.9rem',
                          fontWeight: '500',
                          transition: 'all 0.2s ease',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}
                        onMouseOver={(e) => {
                          e.target.style.backgroundColor = '#edf2f7';
                        }}
                        onMouseOut={(e) => {
                          e.target.style.backgroundColor = '#f7fafc';
                        }}
                      >
                        <FaTimesCircle size={14} />
                        İptal
                      </button>
                      <button
                        onClick={handleUpdate}
                        style={{
                          backgroundColor: '#3182ce',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          padding: '0.6rem 1rem',
                          cursor: 'pointer',
                          fontSize: '0.9rem',
                          fontWeight: '500',
                          boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
                          transition: 'all 0.2s ease',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}
                        onMouseOver={(e) => {
                          e.target.style.backgroundColor = '#2b6cb0';
                          e.target.style.transform = 'translateY(-2px)';
                          e.target.style.boxShadow = '0 4px 8px rgba(0,0,0,0.1)';
                        }}
                        onMouseOut={(e) => {
                          e.target.style.backgroundColor = '#3182ce';
                          e.target.style.transform = 'translateY(0)';
                          e.target.style.boxShadow = '0 2px 6px rgba(0,0,0,0.1)';
                        }}
                      >
                        <FaCheckCircle size={14} />
                        Güncelle
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'flex-start',
                      marginBottom: '20px',
                      borderBottom: '1px solid var(--border-color, #e2e8f0)',
                      paddingBottom: '15px',
                      paddingTop: '5px'
                    }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px'
                      }}>
                        <div style={{
                          width: '42px',
                          height: '42px',
                          borderRadius: '50%',
                          backgroundColor: 'rgba(49, 130, 206, 0.1)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#3182ce'
                        }}>
                          <FaUniversity size={20} />
                        </div>
                        <div style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '2px'
                        }}>
                          <h3 style={{ 
                            margin: '0',
                            fontSize: '1.3rem',
                            fontWeight: '600',
                            color: 'var(--title-color, #1a202c)'
                          }}>{account.firma}</h3>
                          <span style={{ 
                            fontSize: '0.9rem',
                            color: 'var(--secondary-text, #718096)',
                            fontFamily: 'monospace',
                            letterSpacing: '0.5px'
                          }}>{account.hesapNo}</span>
                        </div>
                      </div>
                      
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '15px'
                      }}>
                        <div className="statusBadge" style={{
                          padding: '0.35rem 0.75rem',
                          borderRadius: '9999px',
                          backgroundColor: `${getStatusColor(account.durum)}20`,
                          color: getStatusColor(account.durum),
                          fontSize: '0.8rem',
                          fontWeight: '600',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '5px',
                          textTransform: 'capitalize'
                        }}>
                          <span style={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            backgroundColor: getStatusColor(account.durum)
                          }}></span>
                          {account.durum}
                        </div>
                        
                        <button 
                          style={{ 
                            width: '36px',
                            height: '36px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'var(--secondary-text, #718096)', 
                            cursor: 'pointer',
                            borderRadius: '50%',
                            backgroundColor: '#f7fafc',
                            border: 'none',
                            transition: 'all 0.2s ease',
                          }}
                          onMouseOver={(e) => {
                            e.target.style.backgroundColor = '#ebf8ff';
                            e.target.style.color = '#3182ce';
                            e.target.style.transform = 'scale(1.05)';
                          }}
                          onMouseOut={(e) => {
                            e.target.style.backgroundColor = '#f7fafc';
                            e.target.style.color = 'var(--secondary-text, #718096)';
                            e.target.style.transform = 'scale(1)';
                          }}
                          onClick={() => handleEdit(account)}
                        >
                          <FaEdit size={16} />
                        </button>
                      </div>
                    </div>

                    <div style={{ 
                      display: 'grid', 
                      gridTemplateColumns: '1fr 1fr', 
                      gap: '20px',
                      fontSize: '15px'
                    }}>
                      <div style={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        gap: '15px' 
                      }}>
                        <div style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '4px'
                        }}>
                          <span style={{ color: 'var(--secondary-text, #718096)', fontSize: '14px' }}>
                            Şube
                          </span>
                          <span style={{ fontWeight: '500', color: 'var(--text-color, #333333)' }}>{account.sube}</span>
                        </div>
                        
                        <div style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '4px'
                        }}>
                          <span style={{ color: 'var(--secondary-text, #718096)', fontSize: '14px' }}>
                            Dosya Açıklaması
                          </span>
                          <span style={{ fontWeight: '500', color: 'var(--text-color, #333333)' }}>{account.dosyaAciklama}</span>
                        </div>

                        <div style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '4px'
                        }}>
                          <span style={{ color: 'var(--secondary-text, #718096)', fontSize: '14px' }}>
                            Ödeme/Kesinti
                          </span>
                          <span style={{ fontWeight: '500', color: 'var(--text-color, #333333)' }}>{account.odemeKesinti}</span>
                        </div>
                      </div>

                      <div style={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        gap: '15px' 
                      }}>
                        <div style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '4px'
                        }}>
                          <span style={{ color: 'var(--secondary-text, #718096)', fontSize: '14px' }}>
                            Ödeme Türü
                          </span>
                          <span style={{ fontWeight: '500', color: 'var(--text-color, #333333)' }}>{account.odemeTuru}</span>
                        </div>

                        <div style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '4px'
                        }}>
                          <span style={{ color: 'var(--secondary-text, #718096)', fontSize: '14px' }}>
                            Ödeme Dönemi
                          </span>
                          <span style={{ fontWeight: '500', color: 'var(--text-color, #333333)' }}>{account.odemeDonemi}</span>
                        </div>

                        <div style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '4px'
                        }}>
                          <span style={{ color: 'var(--secondary-text, #718096)', fontSize: '14px' }}>
                            Ödeme Açıklama
                          </span>
                          <span style={{ fontWeight: '500', color: 'var(--text-color, #333333)' }}>{account.odemeAciklama || '-'}</span>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div style={{
          width: '100%',
          maxWidth: '1200px',
          backgroundColor: 'var(--content-bg, #ffffff)',
          borderRadius: '12px',
          padding: '50px 30px',
          textAlign: 'center',
          boxShadow: 'var(--card-shadow, 0 4px 15px rgba(0, 0, 0, 0.05))',
          border: '1px solid var(--border-color, #e2e8f0)'
        }}>
          <div style={{ 
            color: 'var(--secondary-text, #718096)', 
            fontSize: '16px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '15px'
          }}>
            <FaUniversity size={40} style={{ color: 'rgba(49, 130, 206, 0.5)' }} />
            <span>Henüz kayıtlı hesap bulunmamaktadır.</span>
            <button
              onClick={toggleForm}
              style={{
                backgroundColor: '#3182ce',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                padding: '0.75rem 1.25rem',
                marginTop: '10px',
                cursor: 'pointer',
                fontSize: '0.95rem',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'all 0.2s ease',
              }}
              onMouseOver={(e) => {
                e.target.style.backgroundColor = '#2b6cb0';
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.1)';
              }}
              onMouseOut={(e) => {
                e.target.style.backgroundColor = '#3182ce';
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 2px 6px rgba(0, 0, 0, 0.1)';
              }}
            >
              <FaPlus size={12} />
              Yeni Hesap Ekle
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default BankAccountManagementPage; 